import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./smartFilterPanel.module.scss";
import useSmartFilter from "../useSmartFilter";
import SmartFilterLoader from "./SmartFilterLoader";
import SmartFilterSpinner from "./SmartFilterSpinner";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import Portal from "@Components/Portal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import SmartFilterAppliedPills from "../SmartFilterAppliedPills";
import SmartFilterInfoRibbon, { SmartFilterToast } from "../SmartFilterInfoRibbon";
import { SmartFilterLogo, CloseIconCircle, NoResultsIllustration } from "../icons";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";

/**
 * Smart Filter Panel
 * - Desktop: absolute dropdown below filter bar
 * - Mobile: bottom sheet overlay with header
 */
const SmartFilterPanel: React.FC = () => {
  const {
    query,
    isLoading,
    isCountLoading,
    isOpen,
    appliedFilters,
    extractedButNotApplied,
    isIrrelevant,
    message,
    inventoryCount,
    updateQuery,
    submitQuery,
    applySmartFilters,
    clearAll,
    clearResults,
    closePanel,
  } = useSmartFilter();

  const isDesktop = useIsDesktop();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTextareaScrolled, setIsTextareaScrolled] = useState(false);
  // Track whether the query has been modified since the last API call
  const [isQueryDirty, setIsQueryDirty] = useState(!appliedFilters);

  const handleTextareaScroll = useCallback(() => {
    if (textareaRef.current) {
      setIsTextareaScrolled(textareaRef.current.scrollTop > 0);
    }
  }, []);

  // Close panel when clicking outside (desktop only)
  useEffect(() => {
    if (!isOpen || !isDesktop) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closePanel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isDesktop, closePanel]);

  // Prevent page scroll when panel content changes after API response (desktop)
  useEffect(() => {
    if (isDesktop && isOpen && !isLoading && textareaRef.current) {
      // Refocus textarea with preventScroll to avoid browser scrolling page
      textareaRef.current.focus({ preventScroll: true });
    }
  }, [isLoading, isDesktop, isOpen]);

  // iOS keyboard fix: scroll textarea into view when virtual keyboard opens
  useEffect(() => {
    if (isDesktop || !isOpen) return undefined;

    const handleViewportResize = () => {
      if (textareaRef.current && document.activeElement === textareaRef.current) {
        textareaRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };

    window.visualViewport?.addEventListener("resize", handleViewportResize);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleViewportResize);
    };
  }, [isDesktop, isOpen]);

  // Mark query as dirty when user modifies text after receiving results
  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      updateQuery(newValue);
      if (!newValue && textareaRef.current) {
        textareaRef.current.scrollTop = 0;
        clearAll();
        requestAnimationFrame(() => {
          if (!textareaRef.current) return;

          const el = textareaRef.current;

          el.style.display = "none";
          // eslint-disable-next-line no-void
          void el.offsetHeight;
          el.style.display = "";
        });
      }
      if (!isQueryDirty) {
        setIsQueryDirty(true);
        clearResults();
      }
    },
    [updateQuery, clearResults, isQueryDirty],
  );

  const handleClearAll = useCallback(() => {
    clearAll();
    setIsQueryDirty(true);
    gtagEventEmitter({ action: "smart_filter_clear_all", category: PAGE.SEARCH });
    if (textareaRef.current) {
      const el = textareaRef.current;
      // Force iOS to re-render placeholder by toggling a space and clearing it
      el.value = " ";
      el.value = "";
      el.scrollTop = 0;
      // Focus then blur to trigger full placeholder layout recalculation
      el.focus({ preventScroll: true });
      setTimeout(() => {
        el.blur();
      }, 50);
    }
  }, [clearAll]);

  const handleSubmitAndApply = useCallback(() => {
    if (isQueryDirty || !appliedFilters) {
      // Query was modified or no results yet — call the NLP API
      submitQuery();
      setIsQueryDirty(false);
      gtagEventEmitter({
        action: "smart_filter_query_submitted",
        category: PAGE.SEARCH,
        label: query,
      });
    } else if (appliedFilters && Object.keys(appliedFilters).length > 0) {
      // Results are ready — apply the filters
      applySmartFilters();
      gtagEventEmitter({ action: "smart_filter_applied", category: PAGE.SEARCH, label: query });
    }
  }, [isQueryDirty, appliedFilters, applySmartFilters, submitQuery, query]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmitAndApply();
      }
    },
    [handleSubmitAndApply],
  );

  if (!isOpen) return null;

  const hasResults = !isQueryDirty && appliedFilters && Object.keys(appliedFilters).length > 0;
  const isZeroResults = hasResults && inventoryCount === 0;
  const isButtonDisabled = isLoading || isCountLoading || !query.trim() || isZeroResults;
  const isButtonGreyed = (!isLoading && !isCountLoading && !query.trim()) || isZeroResults;

  const getButtonLabel = () => {
    if (isLoading) return <SmartFilterLoader />;
    if (isCountLoading) return <SmartFilterSpinner />;
    if (isZeroResults) return "No Matches";
    if (isIrrelevant) return "Show Properties";
    if (!isQueryDirty && hasResults && inventoryCount) {
      return `Show ${inventoryCount} Properties`;
    }
    return "Apply Filter";
  };

  const panelContent = (
    <div className={styles.container} ref={containerRef}>
      {/* Background pattern overlay */}
      <div className={styles.backgroundPattern} />

      {/* No results state */}
      {isZeroResults && (
        <div className={styles.noResultsState}>
          <NoResultsIllustration />
          <p className={styles.noResultsTitle}>No Accommodation Found</p>
          <p className={styles.noResultsSubtitle}>
            Try removing some of your filters to view more properties
          </p>
        </div>
      )}

      {/* Applied filter pills from NLP response */}
      {hasResults && <SmartFilterAppliedPills />}

      {/* Info ribbon for filters that couldn't be applied */}
      {extractedButNotApplied && extractedButNotApplied.length > 0 && <SmartFilterInfoRibbon />}

      {/* AI Input Field with circular background reveal */}
      <div className={styles.inputWrapper}>
        <div className={styles.inputGlow} />
        <div className={styles.inputInner}>
          {isTextareaScrolled && <div className={styles.inputInnerFade} />}
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            onScroll={handleTextareaScroll}
            placeholder="Try something like: I want to see all the accommodation under £400"
            rows={4}
            maxLength={500}
            disabled={isLoading}
            aria-label="Smart filter search input"
          />
        </div>
        <SmartFilterToast />
      </div>

      {/* Irrelevant message — shown below input */}
      {isIrrelevant && <p className={styles.irrelevantMessage}>{message}</p>}

      {/* Actions row */}
      <div className={styles.actions}>
        <button
          type="button"
          className={
            isIrrelevant || isZeroResults || hasResults || query.trim()
              ? styles.clearButtonActive
              : styles.clearButton
          }
          onClick={handleClearAll}
          disabled={isLoading || (!query.trim() && !hasResults && !isIrrelevant)}
          aria-label="Clear all smart filters"
        >
          Clear all
        </button>

        <button
          type="button"
          className={
            isButtonGreyed || isIrrelevant ? styles.applyButtonDisabled : styles.applyButton
          }
          onClick={handleSubmitAndApply}
          disabled={isButtonDisabled || isIrrelevant}
          aria-label={hasResults ? "Apply smart filters" : "Search with smart filters"}
        >
          {getButtonLabel()}
        </button>
      </div>
    </div>
  );

  // Mobile: render inside a Portal with BottomSheetOverlay
  if (!isDesktop) {
    return (
      <Portal rootId="forPortal">
        <BottomSheetOverlay
          hideBottomSheetOverlay={closePanel}
          isHistoryPush
          name="SmartFilters"
          linePosition="bottom"
          innerClassName={styles.bottomSheetInner}
          size="fitContent"
        >
          <div className={styles.mobileContent}>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <SmartFilterLogo id="sfHeaderIconBlur" />
                <span className={styles.headerTitle}>Smart Filters</span>
              </div>
              <button
                type="button"
                className={styles.headerClose}
                onClick={closePanel}
                aria-label="Close smart filters"
              >
                <CloseIconCircle />
              </button>
            </div>

            {/* No results state */}
            {isZeroResults && (
              <div className={styles.noResultsState}>
                <NoResultsIllustration />
                <p className={styles.noResultsTitle}>No Accommodation Found</p>
                <p className={styles.noResultsSubtitle}>
                  Try removing some of your filters to view more properties
                </p>
              </div>
            )}

            {/* Applied filter pills */}
            {hasResults && <SmartFilterAppliedPills />}

            {/* Info ribbon */}
            {extractedButNotApplied && extractedButNotApplied.length > 0 && (
              <SmartFilterInfoRibbon />
            )}

            {/* AI Input Field */}
            <div className={styles.inputWrapper}>
              <div className={styles.inputGlow} />
              <div className={styles.inputInner}>
                {isTextareaScrolled && <div className={styles.inputInnerFade} />}
                <textarea
                  ref={textareaRef}
                  className={styles.textarea}
                  value={query}
                  onChange={handleQueryChange}
                  onKeyDown={handleKeyDown}
                  onScroll={handleTextareaScroll}
                  placeholder="Try something like: I want to see all the accommodation under £400"
                  rows={4}
                  maxLength={500}
                  disabled={isLoading}
                  aria-label="Smart filter search input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <SmartFilterToast />
            </div>

            {/* Irrelevant message — shown below input */}
            {isIrrelevant && <p className={styles.irrelevantMessage}>{message}</p>}

            {/* Actions row */}
            <div className={styles.actions}>
              <button
                type="button"
                className={
                  isIrrelevant || isZeroResults || hasResults || query.trim()
                    ? styles.clearButtonActive
                    : styles.clearButton
                }
                onClick={handleClearAll}
                disabled={isLoading || (!query.trim() && !hasResults && !isIrrelevant)}
                aria-label="Clear all smart filters"
              >
                Clear all
              </button>

              <button
                type="button"
                className={
                  isButtonGreyed || isIrrelevant ? styles.applyButtonDisabled : styles.applyButton
                }
                onClick={handleSubmitAndApply}
                disabled={isButtonDisabled || isIrrelevant}
                aria-label={hasResults ? "Apply smart filters" : "Search with smart filters"}
              >
                {getButtonLabel()}
              </button>
            </div>
          </div>
        </BottomSheetOverlay>
      </Portal>
    );
  }

  // Desktop: render inline as dropdown
  return panelContent;
};

export default wrapperHOC(SmartFilterPanel, {
  componentName: "SmartFilterPanel",
  showForChina: false,
  showForWhiteLable: true,
});
